import classNames from 'clsx';
import React, { type ReactElement, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { type ListChildComponentProps, areEqual } from 'react-window';
import { anchorRequestDelay, anchorRequestMargin } from 'reactApp/appHelpers/featuresHelpers/galleryFastscroll';
import { useDelayedIntersectionObserver } from 'reactApp/hooks/useDelayedIntersectionObserver';
import { useIntersectionObserver } from 'reactApp/hooks/useInterSectionObserver';
import { galleryAnchorsStatus } from 'reactApp/modules/gallery/gallery.module';
import {
    DATA_LIST_BREADCRUMBS_ID,
    DATA_LIST_EXTRABLOCKS_ID,
    DATA_LIST_GALLERY_TAB_MENU_ID,
    DATA_LIST_STORIES_WIDGET_ID,
} from 'reactApp/ui/Datalist/DataList.ids';
import { FASTCROLL_TYPES_COUNT } from 'reactApp/ui/VirtualList/VirtualList.constants';
import { getThumbTypeForFastscroll } from 'reactApp/ui/VirtualList/VirtualList.helpers.new';
import { VirtualListDivider } from 'reactApp/ui/VirtualListDivider/VirtualListDivider';

import styles from './VirtualList.new.css';

export const VirtualListFastscrollRow = memo(function VirtualListFastscrollRow(props: ListChildComponentProps): ReactElement | null {
    const { index, style, data } = props;
    const { list, itemActiveIndex, indexes, renderItem, loadOnAnchor } = data;

    const extraBlocksInHead = list.filter((block) =>
        [DATA_LIST_BREADCRUMBS_ID, DATA_LIST_STORIES_WIDGET_ID, DATA_LIST_GALLERY_TAB_MENU_ID].includes(block)
    ).length;

    const startIndex = indexes[index].start;
    const lastIndex = Math.min(indexes[index].end, list.length);
    const handleRef = index === itemActiveIndex ? data.handleRef : null;
    const gridType = (extraBlocksInHead ? index - extraBlocksInHead : index) % FASTCROLL_TYPES_COUNT;

    const isDivider: boolean | undefined = list[startIndex].divider;

    const anchorId: string | undefined = list[startIndex].id;
    const isAnchorLoading: boolean | undefined = list[startIndex].isLoading;
    const isAnchorLoaded: boolean | undefined = list[startIndex].isLoaded;

    const singleRowItem =
        (startIndex === lastIndex &&
            [DATA_LIST_EXTRABLOCKS_ID, DATA_LIST_BREADCRUMBS_ID, DATA_LIST_STORIES_WIDGET_ID, DATA_LIST_GALLERY_TAB_MENU_ID].includes(
                list[startIndex]
            )) ||
        isDivider;

    const isBreadCrumbs = startIndex === lastIndex && list[startIndex] === DATA_LIST_BREADCRUMBS_ID;

    const loadAnchorList = useCallback(() => {
        if (anchorId && !isAnchorLoaded && !isAnchorLoading) {
            loadOnAnchor?.(anchorId);
        }
    }, [anchorId, isAnchorLoaded, isAnchorLoading]);

    const loadAnchorOnScrollRef = useDelayedIntersectionObserver({
        onIntersecting: loadAnchorList,
        delay: anchorRequestDelay,
        rootMargin: anchorRequestMargin,
    });

    const dispatch = useDispatch();

    const activeAnchorRef = useIntersectionObserver({
        onIntersecting: () => anchorId && dispatch(galleryAnchorsStatus({ targetAnchorId: null, activeAnchorId: anchorId })),
        rootMargin: `0px 0px -60% 0px`,
        threshold: 1,
    });

    const content: (JSX.Element | null)[] = [];

    const renderItemHelper = useCallback(
        (index, gridIndex) => {
            if (list[index].divider) {
                const dividerText = list[index].text;

                return (
                    <VirtualListDivider
                        ref={activeAnchorRef}
                        key={index}
                        itemClassName={classNames(styles.dividerGrid, styles.dividerGallery)}
                        qaId={dividerText}
                    >
                        {dividerText}
                    </VirtualListDivider>
                );
            }

            const key = String(typeof list[index] === 'string' ? list[index] : list[index].id);

            return (
                <div
                    key={key + index}
                    className={classNames(styles.colItem, {
                        [styles.singleRow]: singleRowItem,
                        // не применяем стиль для хлебных крошек, т.к заголовок начинает "прыгать" по вертикали для разных видов отображения
                        [styles.compensateScroll]: singleRowItem && !isBreadCrumbs,
                    })}
                >
                    {renderItem(list[index], {
                        index: index - extraBlocksInHead,
                        thumbType: getThumbTypeForFastscroll(gridType, gridIndex),
                    })}
                </div>
            );
        },
        [list, renderItem, gridType, extraBlocksInHead, singleRowItem, isBreadCrumbs]
    );

    for (let i = startIndex; i <= lastIndex; i++) {
        content.push(renderItemHelper(i, i - startIndex));
    }

    return (
        <div style={style} ref={handleRef}>
            <div
                className={classNames(styles.fastscrollRow, styles.compensateScroll, {
                    [styles[`fastscrollRow_grid_${gridType}`]]: true,
                    [styles.singleRow]: singleRowItem && !isDivider,
                    [styles.fastscrollDivider]: isDivider,
                })}
                ref={loadAnchorOnScrollRef}
            >
                {content}
            </div>
        </div>
    );
}, areEqual);
